// Risk Level Utilities
// Standardized risk level colors and utilities

// Standardized risk level colors
// critical: #991b1b (dark red)
// high: #ef4444 (red)
// medium: #f59e0b (yellow/amber)
// low: #10b981 (light green)
// very_low: #059669 (dark green)

/**
 * Checks if a risk level is one of the 5 valid values
 * Only accepts exact values: 'critical', 'high', 'medium', 'low', 'very_low'
 * @param {string} riskLevel - The risk level to check
 * @returns {boolean} True if the risk level is one of the 5 valid values
 */
export const isValidRiskLevel = (riskLevel) => {
  if (!riskLevel) return false;
  return riskLevel === "critical" || riskLevel === "high" || riskLevel === "medium" || riskLevel === "low" || riskLevel === "very_low";
};

/**
 * Gets the color for a risk level
 * Standardized colors: critical (#991b1b), high (#ef4444), medium (#f59e0b), low (#10b981), very_low (#059669)
 * @param {string} riskLevel - The risk level (exact value: 'critical', 'high', 'medium', 'low', 'very_low')
 * @returns {string} Hex color code
 */
export const getRiskLevelColor = (riskLevel) => {
  if (!riskLevel) return "#6b7280"; // gray for unknown

  switch (riskLevel) {
    case "critical":
      return "#991b1b";
    case "high":
      return "#ef4444";
    case "medium":
      return "#f59e0b";
    case "low":
      return "#10b981";
    case "very_low":
      return "#059669";
    default:
      return "#6b7280"; // gray for unknown
  }
};

/**
 * Gets the highest risk level from an array of risk levels
 * Priority: critical > high > medium > low > very_low
 * @param {Array<string>} riskLevels - Array of risk level strings (exact values only)
 * @returns {string} Highest risk level or 'n/a' if array is empty
 */
export const getHighestRiskLevel = (riskLevels) => {
  if (!Array.isArray(riskLevels) || riskLevels.length === 0) return "n/a";

  const validLevels = riskLevels.filter((level) => level && typeof level === "string" && isValidRiskLevel(level));

  if (validLevels.length === 0) return "n/a";

  // Check for critical
  if (validLevels.includes("critical")) return "critical";
  // Check for high
  if (validLevels.includes("high")) return "high";
  // Check for medium
  if (validLevels.includes("medium")) return "medium";
  // Check for low
  if (validLevels.includes("low")) return "low";
  // Check for very_low
  if (validLevels.includes("very_low")) return "very_low";

  return "n/a";
};

/**
 * Risk level priority map (lower number = higher priority)
 */
export const riskLevelPriority = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  very_low: 4,
};

/**
 * Gets priority value for a risk level (for sorting)
 * @param {string} riskLevel - The risk level (exact value: 'critical', 'high', 'medium', 'low', 'very_low')
 * @returns {number} Priority value (lower = higher priority)
 */
export const getRiskLevelPriority = (riskLevel) => {
  return riskLevelPriority[riskLevel] ?? 99;
};

/**
 * Risk level gradients for banners/backgrounds
 */
export const riskLevelGradients = {
  critical: "linear-gradient(135deg, #7f1d1d, #991b1b)",
  high: "linear-gradient(135deg, #991b1b, #ef4444)",
  medium: "linear-gradient(135deg, #92400e, #f59e0b)",
  low: "linear-gradient(135deg, #10b981, #059669)",
  very_low: "linear-gradient(135deg, #059669, #047857)",
  default: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
};

/**
 * Gets the gradient for a risk level
 * @param {string} riskLevel - The risk level (exact value: 'critical', 'high', 'medium', 'low', 'very_low')
 * @returns {string} CSS linear-gradient string
 */
export const getRiskLevelGradient = (riskLevel) => {
  if (!riskLevel) return riskLevelGradients.default;
  return riskLevelGradients[riskLevel] || riskLevelGradients.default;
};

/**
 * Risk level badge colors (transparent backgrounds for badges)
 * Light opacity (0.4) for subtle badges
 */
export const riskLevelBadgeColors = {
  critical: "rgba(153, 27, 27, 0.4)",
  high: "rgba(239, 68, 68, 0.4)",
  medium: "rgba(245, 158, 11, 0.4)",
  low: "rgba(16, 185, 129, 0.4)",
  very_low: "rgba(5, 150, 105, 0.4)",
};

/**
 * Risk level badge colors (more opaque for details)
 * Higher opacity (0.9) for prominent badges
 */
export const riskLevelBadgeColorsDetails = {
  critical: "rgba(153, 27, 27, 0.9)",
  high: "rgba(239, 68, 68, 0.9)",
  medium: "rgba(245, 158, 11, 0.9)",
  low: "rgba(16, 185, 129, 0.9)",
  very_low: "rgba(5, 150, 105, 0.9)",
};

/**
 * Gets the badge color for a risk level (light opacity)
 * @param {string} riskLevel - The risk level (exact value: 'critical', 'high', 'medium', 'low', 'very_low')
 * @returns {string} RGBA color string
 */
export const getRiskLevelBadgeColor = (riskLevel) => {
  if (!riskLevel) return "rgba(100, 116, 139, 0.4)"; // gray for unknown
  return riskLevelBadgeColors[riskLevel] || "rgba(100, 116, 139, 0.4)";
};

/**
 * Gets the badge color for a risk level (high opacity for details)
 * @param {string} riskLevel - The risk level (exact value: 'critical', 'high', 'medium', 'low', 'very_low')
 * @returns {string} RGBA color string
 */
export const getRiskLevelBadgeColorDetails = (riskLevel) => {
  if (!riskLevel) return "rgba(100, 116, 139, 0.9)"; // gray for unknown
  return riskLevelBadgeColorsDetails[riskLevel] || "rgba(100, 116, 139, 0.9)";
};

/**
 * Helper function to convert hex color to rgba with opacity
 * @param {string} hex - Hex color code (e.g., "#991b1b")
 * @param {number} alpha - Opacity value (0-1)
 * @returns {string} RGBA color string
 */
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Gets priority color style object with bg, border, text, and gradient
 * Used for consistent styling across UI and PDF
 * @param {string} riskLevel - The risk level (exact value: 'critical', 'high', 'medium', 'low', 'very_low')
 * @returns {Object} Style object with bg, border, text, and gradient properties
 */
export const getRiskLevelPriorityColors = (riskLevel) => {
  const textColor = getRiskLevelColor(riskLevel);
  const defaultStyle = {
    bg: "rgba(107, 114, 128, 0.15)",
    border: "rgba(107, 114, 128, 0.3)",
    text: "#6b7280",
    gradient: riskLevelGradients.default
  };

  if (!riskLevel || !isValidRiskLevel(riskLevel)) {
    return defaultStyle;
  }

  return {
    bg: hexToRgba(textColor, 0.15),
    border: hexToRgba(textColor, 0.3),
    text: textColor,
    gradient: getRiskLevelGradient(riskLevel)
  };
};
